import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@mui/material";
import MAInventoryHeader from "../Component/MAInventoryComponents/MAInventoryHeader";
import MAInventoryTable from "../Component/MAInventoryComponents/MAInventoryTable";
import MAWatchlistPopup from "../Component/MAInventoryComponents/MAWatchlistPopup";
import ConfirmWatchlistDeleteDialog from "../Component/MAInventoryComponents/ConfirmWatchlistDeleteDialog";
import MAInventoryFilterModal from "../Component/MAInventoryComponents/MAInventoryFilterModal";
import CustomizeColumns from "../Component/CustomizeColumns";
import OrderEntry from "../Component/OrderEntry";
import {
  useMAInventoryStore,
  mapLiveMarketDataToRow,
  normalizeDailyProcessStatusPayload,
  filterMAInventoryRows,
  isMAInventoryFilterActive,
  buildDefaultMAInventoryFilters,
} from "../Store/maInventoryStore";
import { useMAWatchlistStore } from "../Store/maWatchlistStore";
import { useMAWatchlistFilterStore } from "../Store/maWatchlistFilterStore";
import { tokens, withAlpha } from "../Common/designTokens";
import { useWebSocket } from "../Utils/WebSocketProvider.jsx";
import { useAuth } from "../Context/AuthProvider";
import useSnackbar from "../Utils/useSnackbar";
import {
  ORDER_SIDES,
  PROTOCOL_TYPES,
  MA_INVENTORY_PREFETCH_PAGE_SIZE,
} from "../Constants/AppConstants";
import {
  MA_MD_DEBUG_PREFIX,
  createMAInventoryTraceId,
  describeCusipCollection,
  normalizeCusipList,
} from "../Utils/maInventoryMarketDataUtils";
import { clearConfigIfOutdated } from "../Utils/columnVersionManager";
import { getPreference } from "../Utils/userPreferenceManger";
import {
  fetchWatchlistDailyProcessStatus,
  fetchWatchlists,
  getLiveMarketdataItems,
  unsubscribeWatchlist,
  insertFrontendErrorLog,
} from "../http/api";
import { downloadMAInventoryData } from "../Component/MAInventoryComponents/utils";
import {
  getWatchlistDisplayName,
  getWatchlistInventoryFailedMessage,
  getWatchlistIdentifier,
  getWatchlistInventoryProcessingMessage,
  getWatchlistStatusUpdatePayload,
  isWatchlistFailed,
  isWatchlistProcessing,
  normalizeWatchlistStatus,
  WATCHLIST_STATUS,
} from "../Utils/maWatchlistStatusUtils";

// Every MA Inventory column is sorted client-side over the full prefetched,
// live-updating dataset — nothing is backend-sorted. Which columns are sortable is
// derived from the column registry's `sortable` flag (see `sortableColumnKeys`), so
// there is no separate hand-maintained list to drift out of sync. The live Bid/Offer
// market-data columns (top-of-book best bid/offer; row.dealers holds the deeper
// ladder) re-order — and can migrate across pages — as their prices tick.

// Stable empty set so "nothing to flash" never triggers a re-render (same ref).
const EMPTY_FLASH_SET = new Set();
// If more than this many rows appear in the visible page in a single update we
// treat it as a bulk change (load / watchlist switch / refetch) and don't flash.
const MAX_ENTRY_FLASH_ROWS = 10;

// Row fields holding date/time values, compared chronologically (not lexically).
const DATE_SORT_COLUMN_KEYS = new Set([
  "maturity",
  "callStartDate",
  "nextCallDate",
  "issueDate",
]);

// Row fields holding credit ratings, compared by credit quality (AAA best → D
// worst) via the rating→value maps from /watchlist/SPRatings-list and
// /watchlist/MoodyRatings-list — NOT alphabetically (alphabetical puts B+ ahead
// of BBB and scrambles the +/- modifiers within a grade).
const RATING_SORT_COLUMN_KEYS = new Set(["sp", "moody"]);

// Standard scales, used only until the rating-list APIs respond (they normally
// return exactly these) — so a rating sort is never alphabetical, even briefly.
const DEFAULT_SP_RATING_ORDER = [
  "AAA", "AA+", "AA", "AA-", "A+", "A", "A-", "BBB+", "BBB", "BBB-",
  "BB+", "BB", "BB-", "B+", "B", "B-", "CCC+", "CCC", "CCC-", "CC", "C", "D",
];
const DEFAULT_MOODY_RATING_ORDER = [
  "Aaa", "Aa1", "Aa2", "Aa3", "A1", "A2", "A3", "Baa1", "Baa2", "Baa3",
  "Ba1", "Ba2", "Ba3", "B1", "B2", "B3", "Caa1", "Caa2", "Caa3", "Ca", "C",
];

// Builds a lookup of normalized rating -> rank (1 = best quality). Keys are
// upper-cased/trimmed so row values match regardless of casing ("Aa1" vs "AA1").
export const buildRatingRankMap = (options, fallbackOrder = []) => {
  const map = new Map();
  if (Array.isArray(options)) {
    options.forEach((option) => {
      const key = `${option?.rating ?? ""}`.trim().toUpperCase();
      const rank = Number(option?.value);
      if (key && Number.isFinite(rank) && !map.has(key)) {
        map.set(key, rank);
      }
    });
  }
  if (!map.size) {
    fallbackOrder.forEach((rating, index) => map.set(rating.toUpperCase(), index + 1));
  }
  return map;
};

const isEmptyLocalSortValue = (value) =>
  value === null || value === undefined || value === "";

// Parses a backend date/time value to a millisecond timestamp. Backend values are
// UTC without a trailing "Z"; append one so they parse as UTC (matches the table's
// date formatting). Returns NaN for unparseable input.
const parseInventoryDateValue = (value) => {
  let valueStr = String(value);
  if (!valueStr.endsWith("Z")) {
    valueStr += "Z";
  }
  return new Date(valueStr).getTime();
};

// Compares two orderable values for a column. Ratings compare by credit-quality
// rank (unknown ratings rank as the smallest value); dates chronologically;
// otherwise numeric when both parse as numbers, else case-insensitive string.
// Blanks and unparseable dates never reach here — the caller sinks them to the
// bottom (see isUnsortableSortValue), so this only ranks real values.
export const compareInventoryValues = (a, b, key, ratingRankMaps = null) => {
  if (RATING_SORT_COLUMN_KEYS.has(key) && ratingRankMaps?.[key]) {
    const ranks = ratingRankMaps[key];
    const rankA = ranks.get(String(a).trim().toUpperCase());
    const rankB = ranks.get(String(b).trim().toUpperCase());
    const aKnown = rankA !== undefined;
    const bKnown = rankB !== undefined;
    if (aKnown && bKnown) return rankA - rankB; // rank 1 = AAA → ascending = best first
    if (!aKnown && bKnown) return -1; // unknown rating sorts as the smallest value
    if (aKnown && !bKnown) return 1;
    // Two unknown ratings: fall back to a stable, case-insensitive string order.
    return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
  }

  if (DATE_SORT_COLUMN_KEYS.has(key)) {
    return parseInventoryDateValue(a) - parseInventoryDateValue(b);
  }

  const aNum = typeof a === "number" ? a : Number(a);
  const bNum = typeof b === "number" ? b : Number(b);
  if (Number.isFinite(aNum) && Number.isFinite(bNum)) {
    return aNum - bNum;
  }

  return String(a).localeCompare(String(b), undefined, { sensitivity: "base" });
};

// A value with no orderable position for this column: it always sorts to the
// BOTTOM regardless of ascending/descending. Blanks for any column, plus
// unparseable dates for date columns (kept consistent with blanks).
const isUnsortableSortValue = (value, key) => {
  if (isEmptyLocalSortValue(value)) return true;
  if (DATE_SORT_COLUMN_KEYS.has(key)) {
    return !Number.isFinite(parseInventoryDateValue(value));
  }
  return false;
};

const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== "";

const DEFAULT_MA_INVENTORY_COLUMN_KEYS = [
  "actions",
  "source",
  "cusip",
  "bidDealer",
  "bidYield",
  "bidQuantity",
  "bidPrice",
  "offerPrice",
  "offerQuantity",
  "offerYield",
  "offerDealer",
  "type",
  "description",
  "subType",
];

// Columns that are position-fixed and cannot be removed via Customize Columns
const FIXED_MA_INVENTORY_COLUMN_KEYS = [
  "actions",
  "source",
  "cusip",
  "bidDealer",
  "bidYield",
  "bidQuantity",
  "bidPrice",
  "offerPrice",
  "offerQuantity",
  "offerYield",
  "offerDealer",
];
const normalizePageWatchlist = (watchlist = {}) => {
  const watchlistId = getWatchlistIdentifier(watchlist);

  return {
    ...watchlist,
    watchlistId,
    id: watchlistId,
    watchlistName: watchlist?.watchlistName ?? "",
    characteristics: watchlist?.characteristics ?? null,
    status: normalizeWatchlistStatus(watchlist?.status),
  };
};

const areWatchlistsEquivalent = (current = [], next = []) => {
  if (current.length !== next.length) {
    return false;
  }

  return current.every((watchlist, index) => {
    const nextWatchlist = next[index];

    return (
      String(getWatchlistIdentifier(watchlist))
        === String(getWatchlistIdentifier(nextWatchlist))
      && `${watchlist?.watchlistName ?? ""}` === `${nextWatchlist?.watchlistName ?? ""}`
      && normalizeWatchlistStatus(watchlist?.status)
        === normalizeWatchlistStatus(nextWatchlist?.status)
    );
  });
};

const MA_INVENTORY_PAGE_HEALTH_TIMEOUT_MS = 31000;
const MAInventory = () => {
  const maInventoryStore = useMAInventoryStore();
  const {
    maInventoryData,
    cusipList,
    totalElements,
    loading,
    liveMarketDataProcessing = false,
    lastUpdatedAt,
    maInventoryPageHealth = {},
    dailyProcessStatus = {},
    maInventoryFilters,
    setMAInventoryFilters = () => {},
    setLoading,
    setLiveMarketDataProcessing = () => {},
    setMAInventoryData,
    setDailyProcessStatus = () => {},
    resetDailyProcessStatus = () => {},
    setMAInventoryPageHealth = () => {},
    resetMAInventoryPageHealth = () => {},
  } = maInventoryStore;
  const {
    connectionState,
    setMAInventoryVisibleCusips,
    clearMAInventoryVisibleCusips,
    setMAInventoryRequestTraceId,
    subscribePageGroup,
    unsubscribePageGroup,
  } = useWebSocket();
  const { token } = useAuth();
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [isDailyProcessCheckPending, setIsDailyProcessCheckPending] = useState(true);
  const isDailyProcessBlocked = dailyProcessStatus?.isBlocking === true;
  const isDataLoading = loading || isInitialRender || isDailyProcessCheckPending;
  const { showError, showInfo, showSuccess, SnackbarComponent } = useSnackbar();
  const customizeButtonRef = useRef(null);
  const preloadWatchlistSharedFilterOptions = useMAWatchlistFilterStore(
    (state) => state.preloadSharedFilterOptions
  );

  const [isPageSubscribed, setIsPageSubscribed] = useState(true);

  useEffect(() => {
    if (isPageSubscribed) {
      subscribePageGroup("maInventoryPage");
      return () => unsubscribePageGroup("maInventoryPage");
    }
  }, [isPageSubscribed, subscribePageGroup, unsubscribePageGroup]);

  useEffect(() => {
    subscribePageGroup("secMaster");
    return () => unsubscribePageGroup("secMaster");
  }, [subscribePageGroup, unsubscribePageGroup]);

  useEffect(() => {
    const handler = () => {
      if (token) preloadWatchlistSharedFilterOptions(token);
    };
    window.addEventListener("secMasterFilterRefresh", handler);
    return () => window.removeEventListener("secMasterFilterRefresh", handler);
  }, [token, preloadWatchlistSharedFilterOptions]);
  // page + itemsPerPage drive client-side pagination only — they no longer trigger
  // a refetch. The full dataset is prefetched once per watchlist.
  const [page, setPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  // Sort is applied entirely client-side over the prefetched rows (all columns).
  const [sortConfig, setSortConfig] = useState({ key: "cusip", direction: "desc" });
  const [ccOpen, setCcOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isWatchlistOpen, setIsWatchlistOpen] = useState(false);
  const [watchlistPopupId, setWatchlistPopupId] = useState(null);
  const [watchlistPopupMode, setWatchlistPopupMode] = useState("edit");
  const [hasWatchlists, setHasWatchlists] = useState(true);
  const [watchlists, setWatchlists] = useState([]);
  const [orderEntryOpen, setOrderEntryOpen] = useState(false);
  const [orderEntryInitialData, setOrderEntryInitialData] = useState(null);
  const [activeWatchlistId, setActiveWatchlistId] = useState(null);
  const watchlistStatusByIdRef = useRef({});
  const activeWatchlistIdRef = useRef(null);
  const previousWatchlistIdRef = useRef(null);
  const tokenRef = useRef(token);
  const lastFetchKeyRef = useRef("");
  // In-flight fetch guard: saving the active watchlist fires a forced fetch from the
  // popup-close handler AND another from the DONE status message moments later.
  // Running both in parallel doubles the prefetch and the whole-watchlist
  // subscription-change burst, and the first traceId gets discarded as a mismatch.
  // Concurrent requests for the same fetchKey coalesce into ONE trailing forced
  // fetch that runs after the in-flight request completes.
  const inFlightFetchRef = useRef(null);
  const [isDeleteWatchlistOpen, setIsDeleteWatchlistOpen] = useState(false);
  const [watchlistToDelete, setWatchlistToDelete] = useState(null);
  const BUTTON_PADDING = "4px 4px"; // changed: reduced horizontal padding
  const BUTTON_MIN_WIDTH = "auto";
  const {
    setCurrentWatchlist,
    setDailyProcessGate,
    resetDailyProcessGate,
  } = useMAWatchlistStore();
  const pageMountedAtRef = useRef(Date.now());
  const dailyProcessGatePromiseRef = useRef(null);

  const maInventoryHealthStatus = useMemo(() => {
    // 1. Hard timeout via the 30s logic checker
    if (maInventoryPageHealth?.issueCode === "heartbeat-timeout") {
      return {
        tone: "warning",
        statusLabel: "Stale",
        message: "Live market data updates are currently unavailable. Please try again later.",
      };
    }

    // 2. We received an actual heartbeat from the backend
    const lastHeartbeatAt = maInventoryPageHealth?.lastHeartbeatAt;
    const pipelineStatus = String(maInventoryPageHealth?.pipelineStatus || "").trim().toUpperCase();
    const modules = Array.isArray(maInventoryPageHealth?.modules) ? maInventoryPageHealth.modules : [];

    if (lastHeartbeatAt) {
      if (pipelineStatus === "OK" && modules.length === 0) {
        return {
          tone: "success",
          statusLabel: "Connected",
          message: `${maInventoryPageHealth?.pipelineDisplayName || "MD Pipeline"} is live and healthy.`,
        };
      }
      
      // Error or Unknown strictly sent by the backend logic updates or there are problematic modules
      if (pipelineStatus !== "OK" || modules.length > 0) {
        return {
          tone: "error",
          statusLabel: "Disconnected",
          message: "We are currently experiencing connectivity issues. Please try again later.",
        };
      }
    }

    // 3. Default initial fallback while waiting to connect or waiting for first health ping
    return {
      tone: "warning",
      statusLabel: "Connecting...",
      message: "Establishing backend health state...",
    };
  }, [connectionState, maInventoryPageHealth]);

  const marketDataHealthDetails = useMemo(() => ({
    tone: maInventoryHealthStatus?.tone || "warning",
    statusLabel: maInventoryHealthStatus?.statusLabel || "Unknown",
    message: maInventoryHealthStatus?.message || "Market data health is currently unavailable.",
    modules: Array.isArray(maInventoryPageHealth?.modules) ? maInventoryPageHealth.modules : [],
    timestamp: maInventoryPageHealth?.messageTimestamp || maInventoryPageHealth?.lastHeartbeatAt || null,
  }), [maInventoryHealthStatus, maInventoryPageHealth]);

  useEffect(() => {
    const originalPayload = maInventoryPageHealth?.originalPayload;
    if (
      originalPayload &&
      Array.isArray(originalPayload.pipelines) &&
      originalPayload.pipelines.length > 0
    ) {
      const pipelineStatus = String(originalPayload.pipelines[0]?.pipelineStatus || "").trim().toUpperCase();
      if (pipelineStatus === "UNKNOWN" || pipelineStatus === "ERROR") {
        console.error("MA Inventory Health Issue Detected:", originalPayload);
        insertFrontendErrorLog({
          page: "MAInventory",
          message: "Market Data Connectivity Issue",
          details: originalPayload,
        });
      }
    }
  }, [maInventoryPageHealth?.originalPayload]);

  useEffect(() => {
    activeWatchlistIdRef.current = activeWatchlistId;
  }, [activeWatchlistId]);

  useEffect(() => {
    watchlistStatusByIdRef.current = watchlists.reduce((accumulator, watchlist) => {
      const watchlistId = getWatchlistIdentifier(watchlist);

      if (watchlistId !== null && watchlistId !== undefined && watchlistId !== "") {
        accumulator[String(watchlistId)] = normalizeWatchlistStatus(watchlist?.status);
      }

      return accumulator;
    }, {});
  }, [watchlists]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (!token) {
      return;
    }

    preloadWatchlistSharedFilterOptions(token).catch((error) => {
      console.error("[ma-inventory] Failed to preload watchlist filter metadata", error);
    });
  }, [preloadWatchlistSharedFilterOptions, token]);

  const hasAttemptedReconnectRef = useRef(false);

  useEffect(() => {
    pageMountedAtRef.current = Date.now();
    hasAttemptedReconnectRef.current = false;
    dailyProcessGatePromiseRef.current = null;
    resetMAInventoryPageHealth();
    resetDailyProcessStatus();
    resetDailyProcessGate();
    setIsDailyProcessCheckPending(true);
    initialFetchDoneRef.current = false;
    lastFetchKeyRef.current = "";

    return () => {
      resetMAInventoryPageHealth();
      resetDailyProcessStatus();
      resetDailyProcessGate();
    };
  }, [resetDailyProcessGate, resetDailyProcessStatus, resetMAInventoryPageHealth]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      const healthState = useMAInventoryStore.getState().maInventoryPageHealth;
      const lastHeartbeatAt = healthState?.lastHeartbeatAt;
      const referenceTimestamp = lastHeartbeatAt
        ? new Date(lastHeartbeatAt).getTime()
        : pageMountedAtRef.current;

      const elapsed = Date.now() - referenceTimestamp;

      // Phase 1: 5-second check if we haven't received a heartbeat yet
      if (
        !lastHeartbeatAt &&
        elapsed >= 3000 &&
        !hasAttemptedReconnectRef.current
      ) {
        hasAttemptedReconnectRef.current = true;
        console.warn("MA Inventory 5s timeout: No initial health heartbeat. Bouncing page subscription...");
        
        // Break the MA Inventory connection
        setIsPageSubscribed(false);
        
        setTimeout(() => {
          // Reconnect it and clear the mount time to give it another chance
          pageMountedAtRef.current = Date.now();
          setIsPageSubscribed(true);
        }, 500);
      }

      // Phase 2: Complete 30-second logic timeout
      if (
        elapsed >= MA_INVENTORY_PAGE_HEALTH_TIMEOUT_MS &&
        healthState?.issueCode !== "heartbeat-timeout"
      ) {
        setMAInventoryPageHealth({
          issueCode: "heartbeat-timeout",
        });
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [setMAInventoryPageHealth]);

  const handleAddOrder = useCallback((rowData) => {
    const payload = {
      protocol: PROTOCOL_TYPES.MARKETAXESS,
      side: ORDER_SIDES.BUY,
    };

    // Preserve both sides so Order Entry can auto-fill qty/price when side changes.
    payload.maInventoryBidDealer = rowData?.bidDealer;
    payload.maInventoryBidQuantity = rowData?.bidQuantity;
    payload.maInventoryBidPrice = rowData?.bidPrice;
    payload.maInventoryOfferPrice = rowData?.offerPrice;
    payload.maInventoryOfferQuantity = rowData?.offerQuantity;
    payload.maInventoryOfferDealer = rowData?.offerDealer;
    payload.maInventoryDealers = rowData?.dealers || [];

    if (hasValue(rowData?.cusip)) {
      payload.cusip = rowData.cusip;
    }

    if (hasValue(rowData?.bidQuantity)) {
      payload.qty = rowData.bidQuantity;
    }

    if (hasValue(rowData?.bidPrice)) {
      payload.price = rowData.bidPrice;
    }

    setOrderEntryInitialData(payload);
    setOrderEntryOpen(true);
  }, [setOrderEntryInitialData, setOrderEntryOpen]);

  const allColumns = useMemo(
    () => [
      {
        key: "actions",
        name: "Actions",
        sortable: false,
        nonDraggable: true,
        nonResizable: true,
        render: (cellData, rowData) => (
          <Button
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleAddOrder(rowData);
            }}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "transparent",
              color: tokens.palette.primary,
              "&:hover": {
                backgroundColor: withAlpha(tokens.palette.primary, 0.1),
              },
              border: `1px solid ${tokens.palette.primary}`,
              minWidth: BUTTON_MIN_WIDTH,
              width: "auto",
              height: "auto",
              minHeight: "auto",
              padding: BUTTON_PADDING,
              fontSize: "0.875rem",
              fontWeight: 700,
              lineHeight: 1,
              textTransform: "none",
              marginRight: 0,
              borderRadius: "8px",
              boxShadow: "none",
            }}
          >
            Add Order
          </Button>
        ),
      },
      {
        key: "source",
        name: "Source",
        sortable: false,
        nonDraggable: true,
        nonResizable: true,
        width: 80,
        minWidth: 80,
        render: () => "MA",
      },
      { key: "cusip", name: "Identifier", sortable: true },
      { key: "bidDealer", name: "Bid Dealer", sortable: true },
      { key: "bidYield", name: "Bid Yield", sortable: true },
      { key: "bidQuantity", name: "Bid Qty", sortable: true },
      { key: "bidPrice", name: "Bid Px", sortable: true },
      { key: "offerPrice", name: "Offer Px", sortable: true },
      { key: "offerQuantity", name: "Offer Qty", sortable: true },
      { key: "offerYield", name: "Offer Yield", sortable: true },
      { key: "offerDealer", name: "Offer Dealer", sortable: true },
      { key: "type", name: "Type", sortable: true },
      { key: "securityCharge", name: "Factored Securities", sortable: true },
      { key: "poolFactor", name: "Factor", sortable: true, width: 95 },
      { key: "description", name: "Desc", sortable: true },
      { key: "subType", name: "Subtype", sortable: true },
      { key: "sp", name: "S&P", sortable: true },
      { key: "moody", name: "Moody's", sortable: true },
      { key: "underlyingIssuerRating", name: "Underlying Issuer Rtg", sortable: true },
      { key: "issuer", name: "Issuer", sortable: true },
      { key: "state", name: "Gov State", sortable: true },
      { key: "sector", name: "Sector", sortable: true },
      { key: "incomeSource", name: "Inc Source", sortable: true },
      { key: "paymentFrequency", name: "Pay Freq", sortable: true },
      { key: "conventionMethod", name: "Conv Method", sortable: true },
      { key: "taxable", name: "Taxable", sortable: true },
      { key: "insured", name: "Insured", sortable: true },
      { key: "callable", name: "Callable", sortable: true },
      { key: "altMinTax", name: "Alt Min Tax", sortable: true },
      { key: "coupon", name: "Coupon", sortable: true },
      { key: "currency", name: "Currency", sortable: true },
      { key: "issueCurrency", name: "Issue Currency", sortable: true },
      { key: "couponType", name: "Coupon Type", sortable: true },
      { key: "perpetual", name: "Perpetual", sortable: true },
      { key: "zeroCoupon", name: "Zero Coupon", sortable: true },
      { key: "maturity", name: "Maturity", sortable: true },
      { key: "callStartDate", name: "Call Start Dt", sortable: true },
      { key: "nextCallDate", name: "Next Call Dt", sortable: true },
      { key: "issueDate", name: "Issue Dt", sortable: true },
    ],
    [BUTTON_MIN_WIDTH, BUTTON_PADDING, handleAddOrder]
  );

  // Single source of truth for which columns are sortable: the `sortable` flag on
  // each column above. Used to reject clicks on non-sortable headers; every other
  // column is sorted client-side over the prefetched dataset (see `sortedData`).
  const sortableColumnKeys = useMemo(
    () =>
      new Set(
        allColumns
          .filter((column) => column.sortable !== false)
          .map((column) => column.key)
      ),
    [allColumns]
  );

  const defaultSelectedColumns = useMemo(() => {
    const defaultKeySet = new Set(DEFAULT_MA_INVENTORY_COLUMN_KEYS);
    return allColumns.filter((column) => defaultKeySet.has(column.key));
  }, [allColumns]);

  const hydrateSelectedColumns = useCallback(
    (columns = []) => {
      const columnsByKey = new Map(allColumns.map((col) => [col.key, col]));
      const fixedKeySet = new Set(FIXED_MA_INVENTORY_COLUMN_KEYS);

      const hydrated = (Array.isArray(columns) ? columns : [])
        .map((col) => columnsByKey.get(col?.key))
        .filter(Boolean);

      if (!hydrated.length) {
        return [...defaultSelectedColumns];
      }

      // Ensure fixed columns are always present (in their allColumns order),
      // even if the user previously removed them before the fixed-column lock was added.
      const hydratedKeys = new Set(hydrated.map((col) => col.key));
      const anyFixedMissing = FIXED_MA_INVENTORY_COLUMN_KEYS.some((key) => !hydratedKeys.has(key));

      if (!anyFixedMissing) return hydrated;

      const fixedColumnsInOrder = allColumns.filter((col) => fixedKeySet.has(col.key));
      const nonFixedHydrated = hydrated.filter((col) => !fixedKeySet.has(col.key));
      return [...fixedColumnsInOrder, ...nonFixedHydrated];
    },
    [allColumns, defaultSelectedColumns]
  );

  const [selectedColumns, setSelectedColumns] = useState(() => {
    clearConfigIfOutdated("ma-inventory", defaultSelectedColumns);
    const saved = getPreference("selectedColumns_ma-inventory", []);
    return hydrateSelectedColumns(saved);
  });

  useEffect(() => {
    setSelectedColumns((prev) => hydrateSelectedColumns(prev));
  }, [hydrateSelectedColumns]);

  const activeRequestsRef = useRef(0);

  const checkDailyProcessStatus = useCallback(async () => {
    if (!token) {
      resetDailyProcessGate();
      setIsDailyProcessCheckPending(false);
      return { shouldContinue: false, isBlocking: false };
    }

    if (dailyProcessGatePromiseRef.current) {
      return dailyProcessGatePromiseRef.current;
    }

    dailyProcessGatePromiseRef.current = (async () => {
      setDailyProcessStatus({ isLoading: true });

      try {
        const response = await fetchWatchlistDailyProcessStatus(token);
        const normalizedStatus = normalizeDailyProcessStatusPayload(response?.data);

        setDailyProcessStatus(normalizedStatus);
        setDailyProcessGate({
          status: normalizedStatus.status,
          isBlocked: normalizedStatus.isBlocking,
          message: normalizedStatus.message,
        });
        setIsDailyProcessCheckPending(false);

        if (normalizedStatus.isBlocking) {
          previousWatchlistIdRef.current = null;
          lastFetchKeyRef.current = "";
          setWatchlists([]);
          setHasWatchlists(true);
          setActiveWatchlistId(null);
          setMAInventoryData([], 0);
        }

        return {
          shouldContinue: !normalizedStatus.isBlocking,
          isBlocking: normalizedStatus.isBlocking,
          status: normalizedStatus.status,
        };
      } catch (error) {
        console.error("Failed to fetch daily process status", error);
        setDailyProcessStatus({
          status: "UNKNOWN",
          message: error?.message || null,
          checkedAt: new Date().toISOString(),
          isLoading: false,
          isBlocking: false,
        });
        setDailyProcessGate({
          status: "UNKNOWN",
          isBlocked: false,
          message: null,
        });
        setIsDailyProcessCheckPending(false);
        return {
          shouldContinue: true,
          isBlocking: false,
          status: "UNKNOWN",
        };
      } finally {
        dailyProcessGatePromiseRef.current = null;
      }
    })();

    return dailyProcessGatePromiseRef.current;
  }, [resetDailyProcessGate, setDailyProcessGate, setDailyProcessStatus, setMAInventoryData, token]);

  const fetchMAInventory = useCallback(async (
    selectedWatchlistId = null,
    previousWatchlistId = null,
    refreshWatchlists = false,
    forceFetch = false
  ) => {
    let startedFetch = null;
    try {
      activeRequestsRef.current += 1;
      setLoading(true);
      setLiveMarketDataProcessing(false);

      let resolvedWatchlists = watchlists;
      if (refreshWatchlists || resolvedWatchlists.length === 0) {
        const watchlistsResponse = await fetchWatchlists(token, 0, 100, "id", "DESC");
        resolvedWatchlists = Array.isArray(watchlistsResponse?.data?.content)
          ? watchlistsResponse.data.content.map(normalizePageWatchlist)
          : [];
        setWatchlists((currentWatchlists) =>
          areWatchlistsEquivalent(currentWatchlists, resolvedWatchlists)
            ? currentWatchlists
            : resolvedWatchlists
        );
      }

      const watchlistIds = new Set(
        resolvedWatchlists
          .map((watchlist) => getWatchlistIdentifier(watchlist))
          .filter((watchlistId) =>
            watchlistId !== null && watchlistId !== undefined && watchlistId !== ""
          )
      );

      const fallbackWatchlist = resolvedWatchlists[0] || null;
      const fallbackWatchlistId = getWatchlistIdentifier(fallbackWatchlist);

      const effectiveWatchlistId =
        selectedWatchlistId && watchlistIds.has(selectedWatchlistId)
          ? selectedWatchlistId
          : activeWatchlistId && watchlistIds.has(activeWatchlistId)
          ? activeWatchlistId
          : fallbackWatchlistId;

      if (!effectiveWatchlistId) {
        setHasWatchlists(false);
        setActiveWatchlistId(null);
        setLiveMarketDataProcessing(false);
        setMAInventoryData([], 0);
        return;
      }

      setHasWatchlists(true);
      if (activeWatchlistId !== effectiveWatchlistId) {
        setActiveWatchlistId(effectiveWatchlistId);
      }

      if (!previousWatchlistIdRef.current) {
        previousWatchlistIdRef.current = effectiveWatchlistId;
      }

      const fetchKey = JSON.stringify({
        watchlistId: effectiveWatchlistId,
      });

      if (!forceFetch && fetchKey === lastFetchKeyRef.current) {
        return;
      }

      if (inFlightFetchRef.current?.key === fetchKey) {
        if (forceFetch) {
          inFlightFetchRef.current.pendingForce = true;
          inFlightFetchRef.current.pendingRefreshWatchlists =
            inFlightFetchRef.current.pendingRefreshWatchlists || refreshWatchlists;
        }
        console.log(
          `${MA_MD_DEBUG_PREFIX} FETCH_COALESCED watchlistId=${effectiveWatchlistId} forceFetch=${forceFetch}`
        );
        return;
      }
      startedFetch = { key: fetchKey, pendingForce: false, pendingRefreshWatchlists: false };
      inFlightFetchRef.current = startedFetch;

      lastFetchKeyRef.current = fetchKey;

      const traceId = createMAInventoryTraceId();
      setMAInventoryRequestTraceId(traceId, "ma-inventory-fetch");
      console.log(
        `${MA_MD_DEBUG_PREFIX} FETCH_REQUEST traceId=${traceId} watchlistId=${effectiveWatchlistId} pageSize=${MA_INVENTORY_PREFETCH_PAGE_SIZE}`
      );

      const effectivePreviousWatchlistId = selectedWatchlistId
        ? (previousWatchlistId ?? previousWatchlistIdRef.current)
        : null;

      // Prefetch the entire watchlist in one page. Sorting/pagination happen
      // client-side, so the backend sort order here is not significant — we pass
      // the default client sort (cusip/DESC) purely for consistency.
      const response = await getLiveMarketdataItems(
        token,
        0,
        MA_INVENTORY_PREFETCH_PAGE_SIZE,
        "cusip",
        "DESC",
        effectiveWatchlistId,
        traceId,
        effectivePreviousWatchlistId
      );

      if (selectedWatchlistId) {
        previousWatchlistIdRef.current = effectiveWatchlistId;
      }

      if (response?.success === false) {
        setLiveMarketDataProcessing(true);
        setMAInventoryData([], 0);
        return;
      }

      if (response && response.success && response.data) {
        const existingRowsByCusip = new Map(
          (useMAInventoryStore.getState().maInventoryData || [])
            .filter((row) => row?.cusip)
            .map((row) => [String(row.cusip).trim().toUpperCase(), row])
        );

        const mappedData = (response.data.content || []).map((item) => {
          const rowCusip = String(item?.secmaster?.cusip || "").trim().toUpperCase();
          const existingRow = existingRowsByCusip.get(rowCusip) || {};
          return mapLiveMarketDataToRow(item, existingRow);
        });

        setLiveMarketDataProcessing(false);
        setMAInventoryData(mappedData, response.data.totalElements || 0);
      } else {
        setLiveMarketDataProcessing(false);
        showError("Failed to fetch MA inventory data");
      }
    } catch (error) {
      if (error?.isMarketDataProcessing) {
        setLiveMarketDataProcessing(true);
        setMAInventoryData([], 0);
        return;
      }

      setLiveMarketDataProcessing(false);
      console.error(error);
      showError(error.message || "Failed to fetch MA inventory data");
    } finally {
      activeRequestsRef.current -= 1;
      if (activeRequestsRef.current <= 0) {
        activeRequestsRef.current = 0;
        setLoading(false);
      }
      if (startedFetch && inFlightFetchRef.current === startedFetch) {
        inFlightFetchRef.current = null;
        if (startedFetch.pendingForce) {
          console.log(`${MA_MD_DEBUG_PREFIX} FETCH_TRAILING_REFRESH key=${startedFetch.key}`);
          fetchMAInventory(null, null, startedFetch.pendingRefreshWatchlists, true);
        }
      }
    }
  }, [
    activeWatchlistId,
    token,
    setLoading,
    setLiveMarketDataProcessing,
    setMAInventoryRequestTraceId,
    setMAInventoryData,
    showError,
    watchlists,
  ]);

  const handleWatchlistChange = useCallback(async (nextWatchlistId) => {
    const normalizedWatchlistId = Number(nextWatchlistId);

    if (!normalizedWatchlistId || normalizedWatchlistId === activeWatchlistId) {
      return;
    }

    previousWatchlistIdRef.current = activeWatchlistId;
    setActiveWatchlistId(normalizedWatchlistId);
    setPage(0);
    setLoading(true);
    await fetchMAInventory(normalizedWatchlistId, activeWatchlistId, false, true);
  }, [activeWatchlistId, fetchMAInventory, setLoading]);

  useEffect(() => {
    const handleDbSubscriptionFailure = (event) => {
      const message =
        event?.detail?.message ||
        "Something went wrong for CUSIP subscriptions. Retrying...";
      showError(message);
    };

    window.addEventListener("maInventoryDbSubscriptionFailure", handleDbSubscriptionFailure);
    return () => {
      window.removeEventListener("maInventoryDbSubscriptionFailure", handleDbSubscriptionFailure);
    };
  }, [showError]);

  useEffect(() => {
    const handleWatchlistStatusUpdate = async (event) => {
      const statusPayload = getWatchlistStatusUpdatePayload(event?.detail);

      if (!statusPayload) {
        return;
      }

      const watchlistIdKey = String(statusPayload.watchlistId);
      const previousStatus = watchlistStatusByIdRef.current[watchlistIdKey];
      const nextStatus = normalizeWatchlistStatus(statusPayload.status);
      const watchlistName = statusPayload.watchlistName?.trim() || "This watchlist";

      watchlistStatusByIdRef.current = {
        ...watchlistStatusByIdRef.current,
        [watchlistIdKey]: nextStatus,
      };

      setWatchlists((currentWatchlists) =>
        currentWatchlists.map((watchlist) =>
          String(getWatchlistIdentifier(watchlist)) === watchlistIdKey
            ? normalizePageWatchlist({
              ...watchlist,
              ...statusPayload,
              watchlistId: statusPayload.watchlistId,
            })
            : watchlist
        )
      );

      if (previousStatus === nextStatus) {
        return;
      }

      const isActiveWatchlist =
        String(activeWatchlistIdRef.current ?? "") === watchlistIdKey;

      if (nextStatus === WATCHLIST_STATUS.PROCESSING) {
        return;
      }

      if (nextStatus === WATCHLIST_STATUS.DONE) {
        if (isActiveWatchlist) {
          showSuccess(`${watchlistName} is ready. Refreshing the latest data.`);
          setPage(0);
          setLoading(true);
          await fetchMAInventory(null, null, true, true);
        }
        return;
      }

      if (nextStatus === WATCHLIST_STATUS.FAILED && isActiveWatchlist) {
        showError(
          `${watchlistName} changes could not be completed. You're still viewing the last available data.`
        );
      }
    };

    window.addEventListener("maWatchlistStatusUpdate", handleWatchlistStatusUpdate);
    return () => {
      window.removeEventListener("maWatchlistStatusUpdate", handleWatchlistStatusUpdate);
    };
  }, [fetchMAInventory, setLoading, showError, showSuccess]);

  const initialFetchDoneRef = useRef(false);
  const prevConnStateRef = useRef(connectionState);

  useEffect(() => {
    if (!token) return;

    let isCancelled = false;

    checkDailyProcessStatus().then((result) => {
      if (!isCancelled && result?.isBlocking) {
        initialFetchDoneRef.current = true;
        setLoading(false);
        setIsInitialRender(false);
      }
    });

    return () => {
      isCancelled = true;
    };
  }, [checkDailyProcessStatus, setLoading, token]);

  useEffect(() => {
    if (!token || isDailyProcessCheckPending || isDailyProcessBlocked) {
      prevConnStateRef.current = connectionState;
      return;
    }

    let triggerFetch = false;

    if (!initialFetchDoneRef.current) {
      if (connectionState === "connected") {
        triggerFetch = true;
      }
    } else {
      if (connectionState === "connected" && prevConnStateRef.current !== "connected") {
        triggerFetch = true;
      }
    }

    prevConnStateRef.current = connectionState;

    if (triggerFetch) {
      initialFetchDoneRef.current = true;
      fetchMAInventory(null, null, false, false).finally(() => setIsInitialRender(false));
    }
  }, [
    connectionState,
    fetchMAInventory,
    isDailyProcessBlocked,
    isDailyProcessCheckPending,
    token,
  ]);

  useEffect(() => {
    if (!token || isDailyProcessCheckPending || isDailyProcessBlocked) return;
    
    const timeoutId = setTimeout(() => {
      if (!initialFetchDoneRef.current) {
        console.warn("WebSocket connection timeout: Fetching MA Inventory data via fallback.");
        initialFetchDoneRef.current = true;
        fetchMAInventory(null, null, false, false).finally(() => setIsInitialRender(false));
      }
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [fetchMAInventory, isDailyProcessBlocked, isDailyProcessCheckPending, token]);

  useEffect(() => {
    if (
      !token
      || !initialFetchDoneRef.current
      || isDailyProcessCheckPending
      || isDailyProcessBlocked
    ) {
      return;
    }

    fetchMAInventory(null, null, false, false);
  }, [
    token,
    activeWatchlistId,
    fetchMAInventory,
    isDailyProcessCheckPending,
    isDailyProcessBlocked,
  ]);

  // Client-side range filter (Bid/Offer Qty + Bid/Offer Px) applied over the
  // prefetched, live-updating rows before sorting/pagination. Re-runs on every
  // tick so filtered membership stays correct as prices/quantities change. When
  // no range is active this returns the same array reference (no-op).
  const filteredData = useMemo(
    () => filterMAInventoryRows(maInventoryData, maInventoryFilters),
    [maInventoryData, maInventoryFilters]
  );

  const hasActiveFilters = useMemo(
    () => isMAInventoryFilterActive(maInventoryFilters),
    [maInventoryFilters]
  );

  // Per-column active-filter flags, so the grid header can show a dot on the
  // specific columns (Bid/Offer Qty & Px) that currently have a bound applied.
  const activeFilterColumnKeys = useMemo(() => {
    const keys = new Set();
    const isBoundSet = (value) =>
      value !== null
      && value !== undefined
      && String(value).trim() !== ""
      && Number.isFinite(Number(value));
    Object.entries(maInventoryFilters || {}).forEach(([key, range]) => {
      if (range && (isBoundSet(range.min) || isBoundSet(range.max))) {
        keys.add(key);
      }
    });
    return keys;
  }, [maInventoryFilters]);

  // Rating rank lookups for the S&P / Moody's sort. The options are preloaded on
  // page mount (preloadSharedFilterOptions); the standard-scale fallback covers
  // the window before they arrive, so a rating sort is never alphabetical.
  const spRatingOptions = useMAWatchlistFilterStore(
    (state) => state?.filterOptions?.spRatingOptions
  );
  const moodyRatingOptions = useMAWatchlistFilterStore(
    (state) => state?.filterOptions?.moodyRatingOptions
  );
  const ratingRankMaps = useMemo(
    () => ({
      sp: buildRatingRankMap(spRatingOptions, DEFAULT_SP_RATING_ORDER),
      moody: buildRatingRankMap(moodyRatingOptions, DEFAULT_MOODY_RATING_ORDER),
    }),
    [spRatingOptions, moodyRatingOptions]
  );

  // Sort the filtered dataset client-side across every column. This runs
  // continuously over the live-updating rows, so bid/offer columns re-order as
  // prices tick and rows can migrate between pages. Array.sort is stable, so rows
  // that compare equal keep their existing (backend) relative order.
  const sortedData = useMemo(() => {
    const { key, direction } = sortConfig;
    if (!key) {
      return filteredData;
    }

    return [...filteredData].sort((rowA, rowB) => {
      const a = rowA?.[key];
      const b = rowB?.[key];
      const aBad = isUnsortableSortValue(a, key);
      const bBad = isUnsortableSortValue(b, key);
      // Blanks (and unparseable dates) always sink to the bottom, in BOTH
      // ascending and descending — direction only orders the real values.
      if (aBad && bBad) return 0;
      if (aBad) return 1;
      if (bBad) return -1;
      const result = compareInventoryValues(a, b, key, ratingRankMaps);
      return direction === "asc" ? result : -result;
    });
  }, [filteredData, sortConfig, ratingRankMaps]);

  // Client-side page slice of the sorted rows — this is what the table renders.
  const pagedData = useMemo(() => {
    const start = page * itemsPerPage;
    return sortedData.slice(start, start + itemsPerPage);
  }, [sortedData, page, itemsPerPage]);

  // --- Row-entry flash -------------------------------------------------------
  // Flash a row when it enters the currently visible page because of a live
  // data change — a filtered-out row that started matching, or an MD-sorted row
  // that crossed a page boundary from another page. We do NOT flash when the
  // visible rows change because the user paged/sorted/filtered/switched
  // watchlist, or on the initial load, since those aren't "a quote moved into
  // view". The row already exists in the dataset; it just relocated into the
  // visible slice.
  const visiblePageCusips = useMemo(
    () => (pagedData || []).map((row) => row?.cusip).filter(Boolean),
    [pagedData],
  );

  // Structural inputs that make a visible-row change user/dataset-driven rather
  // than a live tick. When any of these change we suppress the flash.
  const flashStructuralKey = useMemo(
    () =>
      JSON.stringify({
        page,
        itemsPerPage,
        sortKey: sortConfig.key,
        sortDir: sortConfig.direction,
        watchlistId: activeWatchlistId,
        filters: maInventoryFilters,
      }),
    [page, itemsPerPage, sortConfig, activeWatchlistId, maInventoryFilters],
  );

  const [flashRowCusips, setFlashRowCusips] = useState(EMPTY_FLASH_SET);
  const flashPrevRef = useRef({ cusips: [], structuralKey: null, initialized: false });

  useEffect(() => {
    const prev = flashPrevRef.current;
    const structuralChanged = prev.structuralKey !== flashStructuralKey;
    flashPrevRef.current = {
      cusips: visiblePageCusips,
      structuralKey: flashStructuralKey,
      initialized: true,
    };

    // First render, or a user/dataset-structural change: re-baseline, no flash.
    if (!prev.initialized || structuralChanged) {
      setFlashRowCusips(EMPTY_FLASH_SET);
      return;
    }

    const prevSet = new Set(prev.cusips);
    const entered = visiblePageCusips.filter((cusip) => !prevSet.has(cusip));

    // Nothing new, or a bulk change (refetch/switch) — don't flash. Using the
    // shared empty-set reference lets React bail out of the state update.
    if (entered.length === 0 || entered.length > MAX_ENTRY_FLASH_ROWS) {
      setFlashRowCusips(EMPTY_FLASH_SET);
      return;
    }

    setFlashRowCusips(new Set(entered));
  }, [visiblePageCusips, flashStructuralKey]);

  // Clamp the client-side page to the filtered row count (not the backend total),
  // so applying a filter that shrinks the dataset snaps back to a valid page.
  useEffect(() => {
    const rowCount = sortedData.length;
    const normalizedItemsPerPage = Number(itemsPerPage) || 1;
    const maxPage =
      rowCount > 0
        ? Math.max(0, Math.ceil(rowCount / normalizedItemsPerPage) - 1)
        : 0;

    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [itemsPerPage, page, sortedData.length]);

  // Every prefetched row is subscribed to live market data (not just the current
  // page), so bid/offer values stay live across all pages and client-side sorting
  // by those columns is meaningful for the whole dataset. `cusipList` comes from
  // the store and only changes on a full row-set replacement (fetch/switch), so
  // this reference is stable across ticks — the subscribe set is not rebuilt on
  // every live update.
  //
  // Priority ordering: the rows currently on screen go FIRST. The chunked
  // subscribe pump processes the list in order, so the visible page's data
  // appears immediately while off-page rows fill in behind. `pagedData` is
  // deliberately NOT a dependency — live ticks must not rebuild this list (only
  // order would change, churning the subscription layer every frame). It
  // re-orders only on a row-set replacement or a page/page-size change, reading
  // the page slice current at that moment; a page flip mid-burst therefore
  // re-prioritizes the newly visible rows. Membership never changes here.
  const visibleCusips = useMemo(() => {
    if (!cusipList.length) {
      return cusipList;
    }
    const pageCusips = normalizeCusipList((pagedData || []).map((row) => row?.cusip));
    if (!pageCusips.length) {
      return cusipList;
    }
    const pageSet = new Set(pageCusips);
    return [...pageCusips, ...cusipList.filter((cusip) => !pageSet.has(cusip))];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cusipList, page, itemsPerPage]);

  useEffect(() => {
    console.log(
      `${MA_MD_DEBUG_PREFIX} SUBSCRIBED_CUSIPS count=${visibleCusips.length} cusips=${describeCusipCollection(visibleCusips)}`
    );

    setMAInventoryVisibleCusips(visibleCusips, "ma-inventory-prefetched-rows");
  }, [setMAInventoryVisibleCusips, visibleCusips]);

  useEffect(() => {
    return () => {
      const unsubWatchlistId = activeWatchlistIdRef.current;
      const authToken = tokenRef.current;

      if (authToken && unsubWatchlistId) {
        unsubscribeWatchlist(authToken, unsubWatchlistId).catch((error) => {
          console.error(
            `${MA_MD_DEBUG_PREFIX} WATCHLIST_UNSUBSCRIBE_ERROR watchlistId=${unsubWatchlistId}`,
            error
          );
        });
      }

      console.log(`${MA_MD_DEBUG_PREFIX} PAGE_UNMOUNT clearing visible CUSIPs`);
      clearMAInventoryVisibleCusips("ma-inventory-unmount");
    };
  }, [clearMAInventoryVisibleCusips]);

  const handlePageChange = (event, newPage) => {
    // Client-side pagination over the prefetched rows — no refetch.
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = useCallback((columnKey, direction) => {
    // Ignore clicks on non-sortable columns; every sortable column is sorted
    // client-side over the prefetched dataset — no refetch.
    if (!sortableColumnKeys.has(columnKey)) {
      return;
    }
    // Keep the user on their current page. Sorting re-orders the full dataset
    // client-side and `pagedData` re-slices `sortedData` for the current page,
    // so the correct rows for that page are shown in the new order — jumping
    // back to page 0 on every sort would be wrong. (The clamp effect still
    // snaps back if the page ends up out of range for any reason.)
    setSortConfig({ key: columnKey, direction });
  }, [sortableColumnKeys]);

  const handleApplyFilters = useCallback((nextFilters) => {
    // Filtering is entirely client-side over the prefetched rows — just store
    // the new ranges and reset to the first page of the filtered result.
    setMAInventoryFilters(nextFilters);
    setPage(0);
  }, [setMAInventoryFilters]);

  const handleClearFilters = useCallback(() => {
    setMAInventoryFilters(buildDefaultMAInventoryFilters());
    setPage(0);
  }, [setMAInventoryFilters]);

  const handleDownload = useCallback((format) => {
    // Export what the user is actually looking at (respects the active filter).
    downloadMAInventoryData(filteredData, selectedColumns, format);
  }, [filteredData, selectedColumns]);

  const activeWatchlist = useMemo(
    () =>
      watchlists.find(
        (watchlist) =>
          String(getWatchlistIdentifier(watchlist))
            === String(activeWatchlistId ?? "")
      ) ?? null,
    [activeWatchlistId, watchlists]
  );
  const activeWatchlistDisplayName =
    getWatchlistDisplayName(activeWatchlist) || "This watchlist";
  const activeWatchlistStatusMessage = isWatchlistProcessing(activeWatchlist)
    ? getWatchlistInventoryProcessingMessage(activeWatchlistDisplayName)
    : isWatchlistFailed(activeWatchlist)
    ? getWatchlistInventoryFailedMessage(activeWatchlistDisplayName)
    : "";
  const activeWatchlistStatusSeverity = isWatchlistFailed(activeWatchlist)
    ? "error"
    : "info";

  const handleWatchlistClose = useCallback(async (result = {}) => {
    setIsWatchlistOpen(false);
    setWatchlistPopupId(null);
    setWatchlistPopupMode("edit");
    const resultWatchlistId = result?.watchlistId ?? watchlistPopupId ?? null;
    const shouldRefreshActiveWatchlist =
      Number(resultWatchlistId) === Number(activeWatchlistId);

    if ((result?.saved || result?.watchlistChanged) && token && resultWatchlistId && !shouldRefreshActiveWatchlist) {
      previousWatchlistIdRef.current = activeWatchlistId;
      setActiveWatchlistId(Number(resultWatchlistId));
      setPage(0);
      setLoading(true);
      await fetchMAInventory(Number(resultWatchlistId), activeWatchlistId, true, true);
      return;
    }

    if ((result?.saved || result?.watchlistChanged) && token && shouldRefreshActiveWatchlist) {
      setPage(0);
      setLoading(true);
      await fetchMAInventory(null, null, true, true);
    }
  }, [fetchMAInventory, token, setLoading, watchlistPopupId, activeWatchlistId]);

  const handleWatchlistEdit = useCallback((watchlist) => {
    if (isWatchlistProcessing(watchlist)) {
      showInfo(
        getWatchlistInventoryProcessingMessage(
          getWatchlistDisplayName(watchlist) || "This watchlist"
        )
      );
      return;
    }

    const nextWatchlistId = watchlist?.watchlistId ?? watchlist?.id ?? null;
    if (!nextWatchlistId) {
      setWatchlistPopupMode("edit");
      setIsWatchlistOpen(true);
      return;
    }

    setCurrentWatchlist({
      ...watchlist,
      watchlistId: nextWatchlistId,
      watchlistName: watchlist?.watchlistName ?? "",
    });
    setWatchlistPopupId(nextWatchlistId);
    setWatchlistPopupMode("edit");
    setIsWatchlistOpen(true);
  }, [setCurrentWatchlist, showInfo]);

  const handleCreateWatchlistOpen = useCallback(() => {
    setWatchlistPopupId(null);
    setWatchlistPopupMode("create");
    setIsWatchlistOpen(true);
  }, []);

  const handleManageWatchlistsOpen = useCallback(() => {
    if (!watchlists.length) {
      handleCreateWatchlistOpen();
      return;
    }

    setWatchlistPopupId(activeWatchlistId ?? null);
    setWatchlistPopupMode("edit");
    setIsWatchlistOpen(true);
  }, [activeWatchlistId, handleCreateWatchlistOpen, watchlists.length]);

  const handleWatchlistDelete = useCallback((watchlist) => {
    if (isWatchlistProcessing(watchlist)) {
      showInfo(
        getWatchlistInventoryProcessingMessage(
          getWatchlistDisplayName(watchlist) || "This watchlist"
        )
      );
      return;
    }

    setWatchlistToDelete({
      watchlistId: watchlist?.watchlistId ?? watchlist?.id ?? null,
      watchlistName: watchlist?.watchlistName ?? "",
    });
    setIsDeleteWatchlistOpen(true);
  }, [showInfo]);

  const handleWatchlistDeleteClose = useCallback(async (didDelete) => {
    const deletedId = watchlistToDelete?.watchlistId ?? null;
    setIsDeleteWatchlistOpen(false);
    setWatchlistToDelete(null);

    if (!didDelete || !deletedId) {
      return;
    }

    setLoading(true);
    if (Number(deletedId) === Number(activeWatchlistId)) {
      setPage(0);
      await fetchMAInventory(null, null, true, true);
      return;
    }

    await fetchMAInventory(activeWatchlistId, activeWatchlistId, true, true);
  }, [watchlistToDelete, activeWatchlistId, fetchMAInventory, setLoading]);

  return (
    <>
      <MAInventoryHeader
        loading={isDataLoading}
        totalElements={totalElements}
        lastUpdatedAt={lastUpdatedAt}
        pipelineHealth={maInventoryHealthStatus}
        marketDataHealth={marketDataHealthDetails}
        dailyProcessStatus={dailyProcessStatus}
        isDailyProcessBlocked={isDailyProcessBlocked}
        customizeButtonRef={customizeButtonRef}
        watchlists={watchlists}
        selectedWatchlistId={activeWatchlistId ?? ""}
        activeWatchlistStatusMessage={activeWatchlistStatusMessage}
        activeWatchlistStatusSeverity={activeWatchlistStatusSeverity}
        onWatchlistChange={handleWatchlistChange}
        onWatchlistClick={handleManageWatchlistsOpen}
        onWatchlistEdit={handleWatchlistEdit}
        onWatchlistDelete={handleWatchlistDelete}
        onCreateWatchlistClick={handleCreateWatchlistOpen}
        onColumnsClick={() => setCcOpen(true)}
        onDownloadClick={handleDownload}
        onFilterClick={() => setFilterModalOpen(true)}
        onClearFilters={handleClearFilters}
        hasActiveFilters={hasActiveFilters}
      />
      <MAInventoryTable
        data={pagedData}
        selectedColumns={selectedColumns}
        sortConfig={sortConfig}
        onSort={handleSort}
        page={page}
        itemsPerPage={itemsPerPage}
        totalElements={sortedData.length}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        maInventoryData={maInventoryData}
        loading={isDataLoading}
        hasWatchlists={hasWatchlists}
        isProcessing={
          isWatchlistProcessing(activeWatchlist) || liveMarketDataProcessing
        }
        isDailyProcessBlocked={isDailyProcessBlocked}
        dailyProcessStatus={dailyProcessStatus}
        onCreateWatchlistClick={handleCreateWatchlistOpen}
        onEditWatchlistClick={handleManageWatchlistsOpen}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={handleClearFilters}
        flashRowCusips={flashRowCusips}
        activeFilterColumnKeys={activeFilterColumnKeys}
      />
      <MAWatchlistPopup
        open={isWatchlistOpen}
        onClose={handleWatchlistClose}
        token={token}
        onError={showError}
        onInfo={showInfo}
        onSuccess={showSuccess}
        initialWatchlistId={watchlistPopupId}
        createMode={watchlistPopupMode === "create"}
      />
      <ConfirmWatchlistDeleteDialog
        open={isDeleteWatchlistOpen}
        onClose={handleWatchlistDeleteClose}
        watchlist={watchlistToDelete}
      />
      <MAInventoryFilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={maInventoryFilters}
      />
      <CustomizeColumns
        open={ccOpen}
        onClose={() => setCcOpen(false)}
        allColumns={allColumns}
        selectedColumns={selectedColumns}
        setSelectedColumnsInventory={setSelectedColumns}
        anchorEl={customizeButtonRef.current}
        sectionKey="ma-inventory"
        source="ma-inventory"
        fixedColumnKeys={FIXED_MA_INVENTORY_COLUMN_KEYS}
      />
      {orderEntryOpen && (
        <OrderEntry
          open={orderEntryOpen}
          onClose={() => setOrderEntryOpen(false)}
          source="maInventoryAddOrder"
          initialActiveTab={0}
          initialData={orderEntryInitialData}
        />
      )}
      <SnackbarComponent />
    </>
  );
};

export default MAInventory;
